import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatDateTime } from '@/shared/lib'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  StatusBadge,
} from '@/shared/ui'
import type { DataTableColumnMeta } from '@/shared/ui'

import { sessionsQuery } from '../api/account-api'
import { signOutEverywhere } from '../model/session-lifecycle'
import type { UserSession } from '../model/types'

export function SessionsCard() {
  const { t } = useTranslation(['auth', 'common'])
  const navigate = useNavigate()
  const [isConfirmOpen, setConfirmOpen] = useState(false)
  const { data, isPending, error, refetch } = useQuery(sessionsQuery())

  const revokeAll = useMutation({
    mutationFn: signOutEverywhere,
    // The call ends this session too, so there is nothing left to return to.
    onSuccess: () => navigate({ to: '/login', replace: true }),
  })

  const columns: ColumnDef<UserSession, unknown>[] = [
    {
      accessorKey: 'ip',
      header: t('account.sessions.columns.ip'),
      size: 140,
      meta: { isMono: true } satisfies DataTableColumnMeta,
      cell: ({ row }) => row.original.ip ?? '—',
    },
    {
      accessorKey: 'userAgent',
      header: t('account.sessions.columns.userAgent'),
      // Bounded on purpose. The cell already carries `truncate`, but truncate is
      // `white-space: nowrap`, and under `table-layout: auto` a nowrap cell sets
      // the column's minimum width to the whole string — a 120-character agent
      // made the table about 2000px wide, which pushed the page past the
      // viewport, shifted every card left and took the dock off screen with it.
      // A max-width on the content caps what the column can ask for.
      cell: ({ row }) => (
        <span
          className="block max-w-field-lg truncate"
          // Truncated text with no way to read the rest is a dead cell; the
          // full string is one hover away.
          title={row.original.userAgent ?? undefined}
        >
          {row.original.userAgent ?? '—'}
        </span>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: t('account.sessions.columns.created'),
      size: 170,
      meta: { isMono: true } satisfies DataTableColumnMeta,
      cell: ({ row }) => formatDateTime(row.original.createdAt),
    },
    {
      accessorKey: 'expiresAt',
      header: t('account.sessions.columns.expires'),
      size: 170,
      meta: { isMono: true } satisfies DataTableColumnMeta,
      cell: ({ row }) => formatDateTime(row.original.expiresAt),
    },
    {
      id: 'current',
      // Named, then hidden. An empty `<th>` is an unnamed column, and the
      // header that stood here was rendered as a button with no text at all.
      header: t('common:ui.thisSession'),
      meta: { hideHeader: true } satisfies DataTableColumnMeta,
      size: 110,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.isCurrent ? (
          <StatusBadge tone="info" size="sm">
            {t('account.sessions.current')}
          </StatusBadge>
        ) : null,
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('account.sessions.title')}</CardTitle>
          <CardDescription>{t('account.sessions.description')}</CardDescription>
        </CardHeader>

        <CardContent>
          {/* goauth has no per-session revoke for the user themselves — only
              logout-all — so the rows carry no actions. */}
          <DataTable
            data={data ?? []}
            columns={columns}
            isPending={isPending}
            error={error}
            onRetry={() => void refetch()}
            emptyTitle={t('account.sessions.empty')}
            getRowId={(row) => row.id}
            isClientSide
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
          {t('account.sessions.revokeAll')}
        </Button>
      </div>

      <Dialog open={isConfirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-modal-confirm">
          <DialogHeader>
            <DialogTitle>{t('account.sessions.revokeTitle')}</DialogTitle>
            <DialogDescription>{t('account.sessions.revokeDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              variant="destructive-solid"
              isLoading={revokeAll.isPending}
              onClick={() => revokeAll.mutate()}
            >
              {t('account.sessions.revokeSubmit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
